import { CommonModule } from '@angular/common';
import { Component, input, OnInit, signal } from '@angular/core';
import { EComponentSize, EIcon, ELabelType, IComponentSection } from '../../types';
import { PhotoComponent } from '../photo/photo.component';
import { DynamicFormComponent } from '../dynamic-form/dynamic-form.component';
import { LabelComponent } from '../label/label.component';
import { DTO, Types } from 'shared-lib';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { ToastService } from '../toast/toast.service';
import { catchError, lastValueFrom, map, Observable, of, tap } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { LoadingService } from '../loading/loading.service';
import { IconComponent } from '../icon/icon.component';
import { ButtonComponent } from '../button/button.component';
import { ImageUploaderComponent } from '../image-uploader/image-uploader.component';
import { FileService } from '../../service/files.service';

@Component({
  selector: 'ui-profile',
  standalone: true,
  imports: [CommonModule, LabelComponent, PhotoComponent, DynamicFormComponent, ButtonComponent, IconComponent, ImageUploaderComponent],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss'
})
export class ProfileComponent implements OnInit {
  fields = input<IComponentSection[]>([]);
  loading = signal(true);

  originalUser: DTO.UserDTO | undefined;
  files: DTO.UploadDTO[] = [];
  editable = false;
  avatar?: any;
  EComponentSize = EComponentSize;
  ELabelTypes = ELabelType;
  EAlert = Types.EAlert;
  EIcon = EIcon;
  currentFields = this.fields();

  constructor(
    private http: HttpClient,
    private router: Router,
    private toastService: ToastService,
    private authService: AuthService,
    private loadingService: LoadingService,
    private fileService: FileService,
  ) { }

  async ngOnInit() {
    await this.userData();
    await this.userPhotos();
  }

  get avatarId(): number {
    return Number(this.avatar?.id) || 1;
  }


  async loadAvatar(id: number) {
    let file = await this.fileService.getFileById(id);
    if (file) {
      this.avatar = {
        id,
        path: file.base64
      }
    } else {
      this.avatar = {
        id: id,
        path: undefined
      }
    }
  }

  getFileId(file: any): number {
    return typeof file.id === 'string' ? +file.id : file.id;
  }

  toggleEditable() {
    this.editable = !this.editable;
    this.fillFields();
  }

  fillFields() {
    this.currentFields = [];
    let passwordValue = this.editable ? '' : "Clique em editar para atualizar";
    if (this.originalUser?.avatar) {
      this.loadAvatar(Number(this.originalUser.avatar?.id))
    } else {
      this.avatar = {
        id: 1,
        path: undefined
      }
    }

    let personalInfo = {
      name: 'Informações Pessoais',
      selector: 'personal',
      content: [
        {
          component: 'TextComponent',
          selector: 'name',
          inputs: {
            editable: this.editable,
            placeHolder: 'Nome',
            default: '',
            value: this.originalUser?.name,
            type: Types.EInput.TEXT,
            required: true,
          },
        },
        {
          component: 'TextComponent',
          selector: 'lastName',
          inputs: {
            editable: this.editable,
            placeHolder: 'Sobrenome',
            default: '',
            value: this.originalUser?.lastName,
            type: Types.EInput.TEXT,
            required: true,
          },
        },
        {
          component: 'TextComponent',
          selector: 'document',
          inputs: {
            editable: this.editable,
            placeHolder: 'CPF',
            default: '',
            value: this.originalUser?.document,
            type: Types.EInput.CPF,
            required: true,
          },
        },
        {
          component: 'TextComponent',
          selector: 'phone',
          inputs: {
            editable: this.editable,
            placeHolder: 'Celular',
            default: '',
            value: this.originalUser?.phone,
            type: Types.EInput.PHONE,
            required: true,
          },
        },
        {
          component: 'TextComponent',
          selector: 'email',
          inputs: {
            editable: this.editable,
            placeHolder: 'Email',
            default: '',
            value: this.originalUser?.email,
            type: Types.EInput.EMAIL,
            required: true,
          },
        },
        {
          component: 'TextComponent',
          selector: 'password',
          inputs: {
            editable: this.editable,
            placeHolder: 'Nova senha',
            default: '',
            value: passwordValue,
            type: Types.EInput.PASSWORD,
            required: false,
          },
        },
        {
          component: 'TextComponent',
          selector: 'birthDate',
          inputs: {
            editable: this.editable,
            placeHolder: 'Data de Nascimento',
            default: '',
            value: String(this.originalUser?.birthDate),
            type: Types.EInput.DATE,
            required: true,
          },
        },
      ],
    };
    this.currentFields.unshift(personalInfo);
  }

  async userData() {
    this.loadingService.start();
    let userResponse = await this.authService.getUserInfo();

    if (userResponse){
      this.originalUser = userResponse;
      this.fillFields();
    }else{
      this.toastService.add(
        `Erro ao receber usuário.`,
        30000,
        'warning'
      );
    }
    this.loadingService.stop();
    this.loading.set(false);
  }

  async userPhotos() {
    this.loadingService.start();
    this.files = [];

    let filesUser = await this.fileService.getMultiplesFiles();
    if (filesUser && filesUser.length > 0) {
      filesUser.forEach(async (file) => {
        if (this.originalUser?.avatar?.id === file.id) {
          return;
        }
        let fileDTO = new DTO.UploadDTO();
        fileDTO.id = file.id;
        const fileContent = await this.fileService.loadFile(file.id);
        fileDTO.path = fileContent ?? '';
        fileDTO.mimetype = file.mimetype;
        fileDTO.name = file.name;
        fileDTO.originalname = file.originalname;
        fileDTO.size = file.size;
        this.files.push(fileDTO);
      });
    }
    this.loadingService.stop();
    this.loading.set(false);
  }

  async onPhotoChange(event: any, isAvatar: boolean) {
    this.loadingService.start();
    const imageFile = event.file;
    const imageID = this.avatar?.path ? event.id : undefined;

    if (!imageFile) {
      this.loadingService.stop();
      return;
    }

    let fileResponse = await this.fileService.sendOneFile(imageFile, isAvatar, imageID);

    if (fileResponse) {
      this.toastService.add(
        `Imagem atualizada com êxito!`,
        30000,
        'success'
      );

      this.userData();
      this.userPhotos();

      this.editable = false;
      this.avatar = fileResponse?.avatar;
    } else {
      this.toastService.add(
        'Erro ao enviar a imagem.',
        30000,
        'danger'
      );
    }
    this.loadingService.stop();
  }

  async onPhotoDelete(event: any) {
    this.loadingService.start();

    let fileResponse = await this.fileService.deleteFile(event.id);

    if (fileResponse){
      this.toastService.add(
        'Imagem excluída com êxito!',
        30000,
        'success'
      );
      this.files = this.files.filter((file) => file.id !== event.id);
    }else{
      this.toastService.add(
        'Erro ao excluir a imagem.',
        30000,
        'danger'
      );
    }

    this.loadingService.stop();
  }

  onFormSubmit(event: any) {
    this.loadingService.start();
    const personalData = event['personal'];

    let userDTO = new DTO.UserDTO();
    userDTO.birthDate = personalData.birthDate;
    userDTO.name = personalData.name;
    userDTO.lastName = personalData.lastName;
    userDTO.document = personalData.document;
    userDTO.email = personalData.email;
    userDTO.password = personalData.password ?? undefined;
    userDTO.phone = personalData.phone;

    this.http
      .put<DTO.AuthOutDTO>('userAccount', userDTO)
      .pipe(
        map((response) => {

          if (response.accessToken) {
            localStorage.setItem('JWT_Token', response.accessToken);
            this.authService.init();
          }

          this.userData();

          this.toastService.add(
            `Perfil atualizado com êxito!`,
            30000,
            'success'
          );

          return true;
        }),
        catchError((error) => {
          this.toastService.add(
            error.error.message ?? 'Ocorreu um erro ao atualizar seus dados.',
            30000,
            'danger'
          );
          return of(false);
        })
      )
      .subscribe();

    this.loadingService.stop();
    this.loading.set(false);
  }


  async onImagesUpload(files: File[]) {
    this.loadingService.start();

    let fileResponse = await this.fileService.sendMultiplesFiles(files);

    if (fileResponse){
        this.toastService.add(
          'Fotos enviadas com êxito!',
          30000,
          'success'
        );

        setTimeout(() => {
          this.userPhotos();
        }, 1000);  
    }else{
      this.toastService.add(
        'Ocorreu um erro ao enviar suas fotos.',
        30000,
        'danger'
      );
    }
   
    this.loadingService.stop();
    this.loading.set(false);
  }
}
